import { resolveFeedFolderFrameRect } from '@/helpers/feedFolderAspectFrame';

const MOBILE_CTA_HORIZONTAL_MARGIN_PX = 16;
const MOBILE_CTA_BOTTOM_MARGIN_PX = 16;
const PC_CTA_LEFT_PX = 14.738;
const PC_CTA_BOTTOM_PX = 21;

const toPx = (value) => `${Number(value).toFixed(3).replace(/\.?0+$/, '')}px`;

const resolveMediaOffsets = (wrapperRect, mediaRect) => {
  if (!wrapperRect || !mediaRect) {
    return { leftOffset: 0, rightOffset: 0, bottomOffset: 0 };
  }

  return {
    leftOffset: Math.max(0, mediaRect.left - wrapperRect.left),
    rightOffset: Math.max(0, wrapperRect.right - mediaRect.right),
    bottomOffset: Math.max(0, wrapperRect.bottom - mediaRect.bottom),
  };
};

export const computeCtaHostLayout = ({ isMobile, wrapperRect, mediaRect }) => {
  const { leftOffset, rightOffset, bottomOffset } = resolveMediaOffsets(wrapperRect, mediaRect);

  if (isMobile) {
    return {
      left: toPx(leftOffset + MOBILE_CTA_HORIZONTAL_MARGIN_PX),
      right: toPx(rightOffset + MOBILE_CTA_HORIZONTAL_MARGIN_PX),
      bottom: toPx(bottomOffset + MOBILE_CTA_BOTTOM_MARGIN_PX),
      width: 'auto',
    };
  }

  return {
    left: toPx(leftOffset + PC_CTA_LEFT_PX),
    right: 'auto',
    bottom: toPx(bottomOffset + PC_CTA_BOTTOM_PX),
    width: '73.1%',
  };
};

const resolveActiveMedia = (wrapper) => (
  wrapper.querySelector('img.storeeel-media-image.active, video.storeeel-movie.active')
  || Array.from(wrapper.querySelectorAll('img.storeeel-media-image, video.storeeel-movie')).find((el) => {
    return getComputedStyle(el).display !== 'none';
  })
  || null
);

const resolveIntrinsicSize = (mediaEl) => {
  if (!mediaEl) return null;
  if (mediaEl.tagName === 'IMG') {
    const width = Number(mediaEl.naturalWidth || 0);
    const height = Number(mediaEl.naturalHeight || 0);
    return width > 0 && height > 0 ? { width, height } : null;
  }
  if (mediaEl.tagName === 'VIDEO') {
    const width = Number(mediaEl.videoWidth || 0);
    const height = Number(mediaEl.videoHeight || 0);
    return width > 0 && height > 0 ? { width, height } : null;
  }
  return null;
};

const resolveVisibleMediaRect = (mediaEl) => {
  if (!mediaEl?.getBoundingClientRect) return null;

  const mediaRect = mediaEl.getBoundingClientRect();
  const intrinsic = resolveIntrinsicSize(mediaEl);
  if (!intrinsic) return mediaRect;

  const boxWidth = Number(mediaRect.width || 0);
  const boxHeight = Number(mediaRect.height || 0);
  if (boxWidth <= 0 || boxHeight <= 0) return mediaRect;

  const ratioX = boxWidth / intrinsic.width;
  const ratioY = boxHeight / intrinsic.height;
  const scale = Math.min(ratioX, ratioY);

  const visibleWidth = intrinsic.width * scale;
  const visibleHeight = intrinsic.height * scale;
  const offsetX = (boxWidth - visibleWidth) / 2;
  const offsetY = (boxHeight - visibleHeight) / 2;

  return {
    left: mediaRect.left + offsetX,
    right: mediaRect.left + offsetX + visibleWidth,
    bottom: mediaRect.top + offsetY + visibleHeight,
  };
};

export const updateCtaHostPositionInWrapper = (wrapper, options = {}) => {
  if (!wrapper?.querySelector) return;

  const host = wrapper.querySelector('[data-storeeel-cta-host]');
  if (!host) return;

  const isMobile = Boolean(window.storeeelItems?.isMobile);
  const mediaEl = options.mediaEl || resolveActiveMedia(wrapper);
  const rawMediaRect = mediaEl?.getBoundingClientRect?.() || null;
  const wrapperRect = wrapper.getBoundingClientRect?.() || null;

  // モバイルのストーリー/リール型では、メディアは applyMobileMediaLayout により wrapper 全体を満たす
  // （height:100dvh / width:100%）。ところがコンテンツ切替直後などに「メディアがまだレイアウトされておらず
  // 矩形が極端に小さい（例: 16x11）」状態で位置計算すると、resolveMediaOffsets が画面サイズ分の巨大な
  // オフセットを返し、CTA が画面外（例: --storeeel-cta-bottom:931px / --storeeel-cta-right:428px）へ飛んで
  // 「CTAボタンが消える」状態になる（visibility は visible のまま、座標だけ画面外）。
  // この場合は位置を更新せず、現在の（画面内の）位置を維持し、メディアが wrapper を満たすよう
  // レイアウトされた後続の呼び出しで正しく再計算させる。
  if (isMobile) {
    const rectSize = (r) => {
      if (!r) return null;
      const w = Number.isFinite(r.width)
        ? r.width
        : (Number.isFinite(r.left) && Number.isFinite(r.right) ? r.right - r.left : NaN);
      const h = Number.isFinite(r.height)
        ? r.height
        : (Number.isFinite(r.top) && Number.isFinite(r.bottom) ? r.bottom - r.top : NaN);
      return { w, h };
    };
    const ws = rectSize(wrapperRect);
    const ms = rectSize(rawMediaRect);
    // 双方のサイズを確定できる場合のみ「メディアが wrapper を満たしていない（＝未レイアウトで潰れている）」を判定する。
    // サイズが取得できない場合（width/height 等が無い）は従来どおり計算を続ける。
    const determinable = ws && ms
      && Number.isFinite(ws.w) && Number.isFinite(ws.h)
      && Number.isFinite(ms.w) && Number.isFinite(ms.h)
      && ws.w > 1 && ws.h > 1;
    if (determinable && (ms.w < ws.w * 0.5 || ms.h < ws.h * 0.5)) {
      return;
    }
  }

  // 連携配信（フィードフォルダ）の商品画像は任意比率のため、画像の実端に合わせると
  // 通常コンテンツ（9:16前提）とCTAの位置がずれる。PCで強制しているのと同じ9:16枠を基準にして、
  // 通常コンテンツと同じ位置に揃える。PCは wrapper 自体が既に9:16なので値は変わらない。
  const feedFolderFrameRect = wrapper.dataset?.feedFolderContent === 'true'
    ? resolveFeedFolderFrameRect(wrapperRect)
    : null;

  const measuredMediaRect = feedFolderFrameRect
    || (isMobile ? resolveVisibleMediaRect(mediaEl) : rawMediaRect);
  const layout = computeCtaHostLayout({
    isMobile,
    wrapperRect: wrapperRect || wrapper.getBoundingClientRect?.(),
    mediaRect: measuredMediaRect,
  });

  // CTA の配置基準を常にメディア表示領域に合わせる
  host.style.setProperty('--storeeel-cta-left', layout.left);
  host.style.setProperty('--storeeel-cta-right', layout.right);
  host.style.setProperty('--storeeel-cta-bottom', layout.bottom);
  host.style.setProperty('--storeeel-cta-width', layout.width);
};

export const updateAllCtaHostPositions = (root = document) => {
  if (!root?.querySelectorAll) return;
  root.querySelectorAll('.slide-media-wrapper').forEach((wrapper) => {
    updateCtaHostPositionInWrapper(wrapper);
  });
};
